import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { updateCustomSnippetsUrl } from "../services/updateCustomSnippetsUrl";
import { Typography, AppButton } from "@libs/zapi-ui/custom";
import { Input } from "@libs/zapi-ui/shadcn";
import { Settings, Edit2, Trash2, Check, X } from "lucide-react";

export function CustomSnippetsUrlSection() {
  const { isLabs, customSnippetsUrl, setMe } = useMeStore();
  const [url, setUrl] = useState(customSnippetsUrl || "");
  const [isEditing, setIsEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: (response) => {
      toast.success("Custom snippets URL updated successfully");
      setMe({ customSnippetsUrl: response.data.customSnippetsUrl });
      setIsEditing(false);
    },
    onError: () => {
      toast.error("Failed to update custom snippets URL");
      setUrl(customSnippetsUrl || "");
    }
  });

  useEffect(() => {
    setUrl(customSnippetsUrl || "");

    return () => {
      setUrl(customSnippetsUrl || "");
    };
  }, [customSnippetsUrl]);

  const handleSave = () => {
    const urlToSave = url.trim() === "" ? null : url.trim();
    mutation.mutate({ customSnippetsUrl: urlToSave });
  };

  const handleCancel = () => {
    setUrl(customSnippetsUrl || "");
    setIsEditing(false);
  };

  const handleClear = () => {
    setUrl("");
    mutation.mutate({ customSnippetsUrl: null });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const isValidUrl = (urlString: string) => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  };

  if (!isLabs) {
    return null;
  }

  return (
    <div className="border border-zinc-200 rounded-xl p-6 space-y-4 bg-gradient-to-br from-white to-zinc-50/50">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Settings className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <Typography variant="label1" className="font-semibold text-zinc-900">
            Custom Snippets URL
          </Typography>
          <Typography variant="body2" color="zinc600" className="mt-1">
            Configure a custom URL for fetching AI prompts. When set, this URL will be used instead of the default
            snippets service.
          </Typography>
        </div>
      </div>

      {!isEditing ? (
        <div className="space-y-4">
          <div className="min-h-[50px] flex items-center">
            {customSnippetsUrl ? (
              <div className="w-full space-y-3">
                <div className="p-4 bg-white border border-zinc-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Typography variant="body2" className="font-mono text-sm text-zinc-700">
                        {customSnippetsUrl}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full p-4 border-2 border-dashed border-zinc-200 rounded-lg text-center">
                <Typography variant="body2" color="zinc500" className="text-center">
                  No custom snippets URL configured. Using default service.
                </Typography>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {customSnippetsUrl ? (
              <>
                <AppButton
                  size="sm"
                  appearance="outlined"
                  onClick={handleEdit}
                  disabled={mutation.isPending}
                  className="flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </AppButton>
                <AppButton
                  size="sm"
                  appearance="outlined"
                  onClick={handleClear}
                  disabled={mutation.isPending}
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear
                </AppButton>
              </>
            ) : (
              <AppButton size="sm" onClick={handleEdit} className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configure URL
              </AppButton>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Typography variant="label2" className="font-medium text-zinc-700">
              Snippets URL
            </Typography>
            <Input
              type="url"
              placeholder="https://your-snippets-service.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={mutation.isPending}
              className={`transition-colors ${url && !isValidUrl(url) ? "border-red-300 focus:border-red-500" : ""}`}
            />
            {url && !isValidUrl(url) && (
              <Typography variant="body3" className="text-red-600 mt-1">
                Please enter a valid URL starting with http:// or https://
              </Typography>
            )}
          </div>

          <div className="flex gap-2">
            <AppButton
              size="sm"
              onClick={handleSave}
              disabled={mutation.isPending || (url.trim() !== "" && !isValidUrl(url.trim()))}
              className="flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save
                </>
              )}
            </AppButton>
            <AppButton
              size="sm"
              appearance="outlined"
              onClick={handleCancel}
              disabled={mutation.isPending}
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </AppButton>
          </div>
        </div>
      )}
    </div>
  );
}
